/**
 * `useImageUpload` — encapsulates the BE-FE contract for the
 * tenant-isolated S3 image-upload flow.
 *
 *   1. POST /api/uploads/presign — creates the attachments row + signed PUT URL.
 *   2. PUT  <s3-presigned-url>  — direct browser → S3 upload.
 *   3. POST /api/uploads/{id}/attach — links the row to its parent (when known).
 *   4. Insert markdown `![alt](presigned-get-url)` into the editor.
 *
 * Phase 4 prep limitation: the inserted URL is the immediate presigned
 * GET URL with a 5-minute TTL. Long-term, an embed re-rendered hours
 * later sees a broken image. The follow-up is a render-time URL
 * rewriter or a tenant-aware CloudFront behavior — neither belongs in
 * this initial slice. The markdown body still serializes the URL so
 * the data round-trips through the BE store; the user just needs a
 * fresh fetch to re-render after expiry.
 *
 * The hook returns a stable callback `uploadImage(file) → Promise<{ url, alt }>`
 * that the editor's toolbar invokes. Errors are propagated to the
 * caller as thrown `ApiError`s so the toolbar's try/catch surfaces them.
 *
 * `parentKind` and `parentId` are passed at hook-init time. `parentId`
 * is optional — when omitted, the BE leaves `parent_id` NULL on the
 * attachments row and the FE is expected to call `attachUpload(...)`
 * separately once the parent is known.
 */

import { ApiError, apiClient } from '@/api/client'
import { useCallback, useMemo, useRef } from 'react'
import { putWithProgress } from './put-with-progress'
import type { ParentKind } from './types'
import { markUploadFinished, markUploadStarted } from './upload-registry'
import type { UploadCallOptions } from './use-file-upload'

/** Per-call options plus a parent id that wins over the hook-level one. */
export interface ImageUploadCallOptions extends UploadCallOptions {
  /**
   * Presign against this parent instead of the hook's. For a caller whose
   * parent only comes into existence after the hook was set up (an avatar
   * picked in a create dialog, uploaded once the new user's id is known).
   */
  parentId?: string
}

export interface ImageUploadResult {
  attachmentId: string
  /** Full URL embeddable in `<img src>`. 5-minute TTL. */
  url: string
  alt: string
  s3Key: string
}

export interface UseImageUploadOptions {
  /** What kind of surface this image is being attached to. */
  parentKind: ParentKind
  /**
   * The parent's UUID, when already saved (e.g. an existing card).
   * Omit when uploading inside a not-yet-saved comment composer; the
   * caller is responsible for invoking `attachUpload(...)` once the
   * parent saves.
   */
  parentId?: string | null
}

interface PresignResponse {
  data: {
    key: string
    uploadUrl: string
    requiredHeaders: Record<string, string>
    attachmentId: string
    /**
     * Set when the upload landed in the public-assets CDN bucket
     * (`parentKind='profile'`). When present, the upload result returns
     * this URL directly and the FE skips the signed-GET round trip.
     * Null for tenant-bucket uploads.
     */
    publicUrl: string | null
  }
}

interface SignedGetResponse {
  data: {
    url: string
    contentType: string
    originalFilename: string
  }
}

/**
 * Presign + PUT + (optional) attach + signed-GET in one call. Returns
 * the URL the editor should embed.
 */
export const useImageUpload = ({ parentKind, parentId }: UseImageUploadOptions) => {
  // Re-stabilize the parentId ref so consumers passing a possibly-null
  // value don't re-mount the editor's onChange.
  const parentIdRef = useRef<string | null | undefined>(parentId ?? null)
  parentIdRef.current = parentId ?? null

  const uploadImage = useCallback(
    async (file: File, options?: ImageUploadCallOptions): Promise<ImageUploadResult> => {
      // Step 1: presign — server constructs the canonical key + creates
      // the attachments row.
      const presign = await apiClient<PresignResponse>('/api/uploads/presign', {
        method: 'POST',
        body: {
          parentKind,
          parentId: options?.parentId ?? parentIdRef.current ?? undefined,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        },
        signal: options?.signal,
      })
      const { uploadUrl, requiredHeaders, attachmentId, publicUrl } = presign.data
      options?.onPresigned?.(attachmentId)

      // Step 2: PUT the file directly to S3. Our auth + tenant headers are
      // deliberately absent — they'd invalidate the S3 signature.
      //
      // Registered in the registry around the transfer so the unload guard
      // covers this hook's callers too; keyed on the attachment id, which
      // exists only after presign (and before it there are no bytes to
      // lose). `finally` because a missed exit leaks an entry into a
      // module-scope registry nothing collects.
      markUploadStarted(attachmentId)
      try {
        const put = await putWithProgress({
          url: uploadUrl,
          file,
          headers: requiredHeaders,
          signal: options?.signal,
          onProgress: options?.onProgress,
        })
        if (put.status < 200 || put.status >= 300) {
          // Error messages stay English — they are dev-fallback only per
          // the `[FE/i18n] BE error codes are canonical` rule. The UI
          // surface above (UploadButton) catches and renders a localized
          // user-facing message.
          throw new ApiError(
            `S3 upload failed: ${put.status} ${put.statusText}`,
            put.status,
            put.responseText,
          )
        }
      } finally {
        markUploadFinished(attachmentId)
      }

      // Avatar uploads (`parentKind='profile'`) land in the public-assets
      // bucket and the BE returns the absolute CDN URL up-front. No
      // signed-GET round trip needed — the URL is permanent until the
      // user replaces the avatar (and then a new UUID is minted).
      if (publicUrl) {
        return {
          attachmentId,
          url: publicUrl,
          alt: file.name,
          s3Key: presign.data.key,
        }
      }

      // Tenant-bucket uploads need a fresh presigned GET URL (the file
      // is not publicly readable). `?as=json` returns the URL string
      // rather than the default 302 redirect so we can drop it into a
      // markdown image src or feed it to a TipTap node.
      const signed = await apiClient<SignedGetResponse>(
        `/api/uploads/${attachmentId}/url?as=json`,
        { method: 'GET' },
      )

      return {
        attachmentId,
        url: signed.data.url,
        alt: file.name,
        s3Key: presign.data.key,
      }
    },
    [parentKind],
  )

  /**
   * Two-phase completion — call this after the parent is saved.
   * Returns the updated attachment row.
   */
  const attachUpload = useCallback(
    async (attachmentId: string, finalParentId: string): Promise<void> => {
      await apiClient(`/api/uploads/${attachmentId}/attach`, {
        method: 'POST',
        body: { parentId: finalParentId },
      })
    },
    [],
  )

  return useMemo(() => ({ uploadImage, attachUpload }), [uploadImage, attachUpload])
}
